// DTOs de C10 — configuración de SKUs y capacidades (coordinador).
//
// 3 endpoints principales:
//   - GET    /api/coordinador/configuracion/skus              — lista con filtros
//   - POST   /api/coordinador/configuracion/skus              — crear nuevo SKU
//   - PATCH  /api/coordinador/configuracion/skus/:id          — editar SKU
//   - GET    /api/coordinador/configuracion/skus/:id          — detalle + matriz líneas
//   - PUT    /api/coordinador/configuracion/skus/:id/lineas/:lineaId
//                                                              — upsert capacidad
//   - DELETE /api/coordinador/configuracion/skus/:id/lineas/:lineaId
//                                                              — quitar mapping
import { z } from 'zod';
// =============================================================================
// SKU base
// =============================================================================
export const skuConfigSchema = z.object({
    id: z.string().uuid(),
    cliente_id: z.string().uuid(),
    cliente_nombre: z.string(),
    codigo_cliente: z.string(),
    codigo_interno: z.string().nullable(),
    descripcion: z.string(),
    formato: z.string(),
    activo: z.boolean(),
    /** Cantidad de líneas con capacidad configurada. */
    lineas_configuradas: z.number().int().min(0),
    /**
     * Capacidad estándar representativa del SKU (MAX de
     * capacidad_estandar_unidades_hora entre sus líneas configuradas).
     * null si el SKU no tiene ninguna línea configurada. Para la columna
     * "CAP. ESTÁNDAR" del listado.
     */
    capacidad_estandar_representativa: z.number().nullable(),
    /**
     * Precio unitario vigente (precios_sku con vigente_hasta IS NULL), en COP.
     * null si el SKU no tiene precio cargado O si el usuario no puede ver costos
     * (capacidad puede_ver_costos). El gate duro vive en el backend.
     */
    precio_vigente: z.number().nullable(),
    /** Fecha (YYYY-MM-DD) desde la que rige el precio vigente. null si no hay. */
    precio_vigente_desde: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});
// =============================================================================
// Precio del SKU — vigencia + histórico (WS-5)
// =============================================================================
/** Una fila del histórico de precios de un SKU (tabla precios_sku). */
export const precioHistoricoItemSchema = z.object({
    id: z.string().uuid(),
    precio_unitario_cop: z.number(),
    vigente_desde: z.string(),
    vigente_hasta: z.string().nullable(),
    /** Nombre de quien registró el cambio (columna "CAMBIÓ" en la UI). */
    registrado_por_nombre: z.string().nullable(),
});
/**
 * Payload para fijar un nuevo precio. Crea una NUEVA vigencia: cierra la fila
 * vigente (vigente_hasta = vigente_desde − 1) e inserta la nueva. NUNCA pisa la
 * historia. `vigente_desde` debe ser > la vigencia abierta actual (si existe).
 */
export const precioUpsertSchema = z.object({
    precio_unitario_cop: z.number().positive().max(99999999999),
    vigente_desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha YYYY-MM-DD'),
    /**
     * Planta a la que aplica el nuevo precio. Si se omite, se deriva de la planta
     * del coordinador; obligatorio si el actor abarca >1 planta (admin/directivo).
     */
    planta_id: z.string().uuid().optional(),
});
// =============================================================================
// Bulk de precios (importación Excel) — POST /configuracion/skus/precios/bulk
// =============================================================================
//
// Una vigencia única `vigente_desde` para todo el lote + un precio por SKU.
// Upsert por (sku, planta): si ya hay vigencia abierta esa misma fecha → corrige
// el precio en el lugar; si la abierta es anterior → la cierra y abre la nueva;
// si no hay → inserta. Atómico (todas o ninguna). planta del actor (o planta_id).
export const precioBulkItemSchema = z.object({
    sku_id: z.string().uuid(),
    precio_unitario_cop: z.number().positive().max(99999999999),
});
export const precioBulkSchema = z.object({
    vigente_desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha YYYY-MM-DD'),
    planta_id: z.string().uuid().optional(),
    items: z.array(precioBulkItemSchema).min(1, 'Mínimo 1 fila').max(5000, 'Máximo 5000 filas'),
});
export const precioBulkResultSchema = z.object({
    /** Filas aplicadas (insert/update/close-open). */
    procesadas: z.number().int().min(0),
    errores: z.array(z.object({ sku_id: z.string(), mensaje: z.string() })),
});
// =============================================================================
// Capacidad por línea
// =============================================================================
export const skuLineaCapacidadSchema = z.object({
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    planta_nombre: z.string(),
    area_nombre: z.string(),
    capacidad_estandar_golpes_min: z.number().min(0).nullable(),
    capacidad_estandar_unidades_hora: z.number().min(0),
    personas_estandar_turno: z.number().int().min(1).max(50),
    bache_minimo: z.number().min(0).nullable(),
    golpes_min_aplica: z.boolean(),
});
/** Línea del scope del coordinador donde el SKU NO está aún configurado. */
export const lineaCandidataSchema = z.object({
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    planta_nombre: z.string(),
    area_nombre: z.string(),
});
// =============================================================================
// Query y create payloads
// =============================================================================
export const skusListQuerySchema = z.object({
    cliente_id: z.string().uuid().optional(),
    activo: z.enum(['true', 'false']).optional(),
    /** Búsqueda por código_cliente, código_interno o descripcion (LIKE %q%). */
    q: z.string().max(100).optional(),
    /**
     * Filtra a los SKUs que tengan capacidad configurada en esta línea
     * (EXISTS sobre skus_lineas). El id viene del select de líneas del scope.
     */
    linea_id: z.string().uuid().optional(),
});
export const skuCreateSchema = z.object({
    cliente_id: z.string().uuid(),
    codigo_cliente: z.string().min(1).max(50),
    codigo_interno: z.string().max(50).nullable().optional(),
    descripcion: z.string().min(1).max(200),
    formato: z.string().min(1).max(50),
});
export const skuUpdateSchema = z.object({
    codigo_interno: z.string().max(50).nullable().optional(),
    descripcion: z.string().min(1).max(200).optional(),
    formato: z.string().min(1).max(50).optional(),
    activo: z.boolean().optional(),
});
export const skuLineaUpsertSchema = z
    .object({
    capacidad_estandar_unidades_hora: z.number().positive(),
    personas_estandar_turno: z.number().int().min(1).max(50),
    bache_minimo: z.number().positive().nullable().optional(),
    golpes_min_aplica: z.boolean(),
    capacidad_estandar_golpes_min: z.number().positive().nullable().optional(),
})
    .refine((d) => (d.golpes_min_aplica === true &&
    d.capacidad_estandar_golpes_min !== null &&
    d.capacidad_estandar_golpes_min !== undefined) ||
    (d.golpes_min_aplica === false &&
        (d.capacidad_estandar_golpes_min === null ||
            d.capacidad_estandar_golpes_min === undefined)), {
    message: 'capacidad_estandar_golpes_min debe estar presente si y solo si golpes_min_aplica = true',
    path: ['capacidad_estandar_golpes_min'],
});
// =============================================================================
// Bulk import de capacidades (matriz) — POST /configuracion/skus/lineas/bulk
// =============================================================================
//
// Cada item = una celda SKU × Línea (igual que el upsert individual, pero con
// sku_id y linea_id en el cuerpo). El FE parsea el Excel (SheetJS), resuelve
// código_cliente → sku_id y código/nombre de línea → linea_id, valida cada fila
// en el preview y solo manda las cargables. El backend revalida scope/existencia
// de TODAS las filas ANTES de aplicar: si alguna falla, no escribe ninguna
// (atómico). Sin pisado silencioso: cada celda trae todos sus campos.
export const skuLineaBulkItemSchema = z
    .object({
    sku_id: z.string().uuid(),
    linea_id: z.string().uuid(),
    capacidad_estandar_unidades_hora: z.number().positive(),
    personas_estandar_turno: z.number().int().min(1).max(50),
    bache_minimo: z.number().positive().nullable().optional(),
    golpes_min_aplica: z.boolean(),
    capacidad_estandar_golpes_min: z.number().positive().nullable().optional(),
})
    .refine((d) => (d.golpes_min_aplica === true &&
    d.capacidad_estandar_golpes_min !== null &&
    d.capacidad_estandar_golpes_min !== undefined) ||
    (d.golpes_min_aplica === false &&
        (d.capacidad_estandar_golpes_min === null ||
            d.capacidad_estandar_golpes_min === undefined)), {
    message: 'capacidad_estandar_golpes_min debe estar presente si y solo si golpes_min_aplica = true',
    path: ['capacidad_estandar_golpes_min'],
});
export const skuLineasBulkSchema = z.object({
    items: z.array(skuLineaBulkItemSchema).min(1, 'Mínimo 1 fila').max(5000, 'Máximo 5000 filas'),
});
export const skuLineasBulkResultSchema = z.object({
    /** Filas aplicadas (upsert). 0 si hubo errores de validación (atómico). */
    procesadas: z.number().int().min(0),
    /** Errores por fila de scope/existencia detectados en backend. */
    errores: z.array(z.object({
        sku_id: z.string(),
        linea_id: z.string(),
        mensaje: z.string(),
    })),
});
// =============================================================================
// Recursos compartidos
// =============================================================================
const clienteResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
});
const lineaResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    planta_nombre: z.string(),
    area_nombre: z.string(),
});
// =============================================================================
// Response: lista
// =============================================================================
export const coordinadorSkusListSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    recursos: z.object({
        clientes: z.array(clienteResumenSchema),
    }),
    /** Total sin paginar (en MVP no paginamos, el dataset es chico). */
    total: z.number().int().min(0),
    skus: z.array(skuConfigSchema),
});
// =============================================================================
// Response: detalle
// =============================================================================
export const coordinadorSkuDetalleSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    sku: skuConfigSchema,
    /** Capacidades configuradas en líneas del scope del coordinador. */
    lineas_configuradas: z.array(skuLineaCapacidadSchema),
    /** Líneas del scope donde el SKU aún no está configurado. */
    lineas_candidatas: z.array(lineaCandidataSchema),
    /**
     * Histórico de precios del SKU (descendente por vigencia). Vacío si el
     * usuario no puede ver costos (gate duro en backend) o no hay precios.
     */
    precio_historico: z.array(precioHistoricoItemSchema),
});
// =============================================================================
// Response: matriz capacidades (SKU × línea)
// =============================================================================
//
// GET /api/coordinador/configuracion/skus/matriz
// Grilla de capacidades: filas = SKUs, columnas = líneas del scope del
// coordinador. Cada celda = capacidad_estandar_unidades_hora (número) o null
// si el SKU no está configurado en esa línea.
export const matrizColumnaLineaSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
});
export const matrizFilaSkuSchema = z.object({
    sku_id: z.string().uuid(),
    codigo_cliente: z.string(),
    descripcion: z.string(),
    /** Mapa linea_id → capacidad_estandar_unidades_hora (o null si no configurada). */
    capacidades: z.record(z.string().uuid(), z.number().nullable()),
    /**
     * Precio unitario vigente del SKU (COP) para la planta del actor, con
     * fallback al tenant-wide. null si no hay precio O si el usuario no puede ver
     * costos (gate duro en backend). Lo usa el export a Excel.
     */
    precio_vigente: z.number().nullable(),
});
/**
 * Configuración COMPLETA de una celda configurada (SKU × línea del scope).
 *
 * `filas[].capacidades` alcanza para pintar la grilla, pero NO para exportar a
 * Excel: el bulk import hace upsert de la celda entera, así que un export que
 * solo trajera la capacidad pisaría personas/golpes/bache al reimportarse. Por
 * eso el endpoint devuelve además todos los campos de cada celda configurada.
 */
export const matrizCeldaConfigSchema = z.object({
    sku_id: z.string().uuid(),
    linea_id: z.string().uuid(),
    capacidad_estandar_unidades_hora: z.number(),
    /**
     * Nullable a propósito: la columna en BD lo admite (filas históricas cargadas
     * antes de que el campo fuera obligatorio en la UI). Si viniera null, el
     * export deja la celda vacía y el import la marca en error — que es lo
     * correcto: el dato realmente falta, no lo inventamos.
     */
    personas_estandar_turno: z.number().int().nullable(),
    golpes_min_aplica: z.boolean(),
    capacidad_estandar_golpes_min: z.number().nullable(),
    bache_minimo: z.number().nullable(),
});
export const coordinadorSkusMatrizSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    /** Columnas = líneas del scope del coordinador, ordenadas. */
    lineas: z.array(matrizColumnaLineaSchema),
    /** Filas = SKUs activos del tenant. */
    filas: z.array(matrizFilaSkuSchema),
    /** Celdas configuradas (solo las que existen en skus_lineas dentro del scope). */
    celdas: z.array(matrizCeldaConfigSchema),
});
//# sourceMappingURL=coordinador_configuracion_skus.js.map